// infcode_change - v1 Docs Knowledge migration
import { createHash, randomUUID } from "crypto"
import fs from "fs/promises"
import path from "path"
import * as Log from "@opencode-ai/core/util/log"
import { KnowledgeStorage } from "@/infcode/knowledge/storage"
import {
  KNOWLEDGE_ENTITY_ID_PATTERN,
  Knowledge as KnowledgeSchema,
  type Knowledge,
  type KnowledgeDocument,
} from "@/infcode/knowledge/types"
import { errorMessage, exists, isDirectory, isFile, isRecord, writeBackup } from "./util"

const LOG_PREFIX = "[infcode:migration:knowledge]"
const log = Log.create({ service: LOG_PREFIX })

type KnowledgeMigrationReason =
  | "knowledge-root-missing"
  | "knowledge-index-invalid"
  | "config-missing"
  | "config-invalid"
  | "name-conflict"
  | "target-resumed"
  | "document-missing"
  | "document-unreadable"
  | "no-readable-documents"
  | "rewrite-failed"
  | "legacy-preserved"

type KnowledgeMigrationDocumentItem = {
  status: "migrated" | "skipped" | "failed"
  legacyDocumentId?: string
  mappedDocumentId?: string
  name?: string
  sourcePath?: string
  targetPath?: string
  reason?: KnowledgeMigrationReason
  message?: string
}

export type KnowledgeMigrationItem = {
  status: "migrated" | "skipped" | "failed"
  sourcePath: string
  targetPath?: string
  backupPath?: string
  legacyKnowledgeId?: string
  mappedKnowledgeId?: string
  name?: string
  reason?: KnowledgeMigrationReason
  message?: string
  documents?: KnowledgeMigrationDocumentItem[]
}

export type KnowledgeMigrationReport = {
  summary: {
    migrated: number
    skipped: number
    failed: number
    documentsMigrated: number
    documentsSkipped: number
    documentsFailed: number
  }
  items: KnowledgeMigrationItem[]
}

type LegacyKnowledgeSummary = {
  id: string
  name: string
  createdTime?: string
  updatedTime?: string
}

type LegacyKnowledgeConfig = {
  name: string
  createdTime?: string
  updatedTime?: string
  documents: Record<string, unknown>[]
  originalText: string
}

type PreparedDocument = {
  document: KnowledgeDocument
  content: string
  report: KnowledgeMigrationDocumentItem
}

export namespace KnowledgeMigration {
  export type Item = KnowledgeMigrationItem
  export type Report = KnowledgeMigrationReport

  export async function run(input: { home: string }): Promise<KnowledgeMigrationReport> {
    const report = createReport()
    const knowledgeRoot = path.join(input.home, ".infcode", "knowledge")
    if (!(await isDirectory(knowledgeRoot))) return report

    const indexPath = path.join(knowledgeRoot, "knowledge.json")
    if (!(await exists(indexPath))) return report

    let originalIndex: string
    try {
      originalIndex = await fs.readFile(indexPath, "utf8")
    } catch (err) {
      addKnowledgeItem(report, {
        status: "failed",
        sourcePath: indexPath,
        reason: "knowledge-index-invalid",
        message: errorMessage(err),
      })
      return report
    }

    const parsedIndex = parseIndex(originalIndex)
    if (!parsedIndex.ok) {
      addKnowledgeItem(report, {
        status: "failed",
        sourcePath: indexPath,
        reason: "knowledge-index-invalid",
        message: parsedIndex.message,
      })
      return report
    }

    const validRows: Knowledge[] = []
    const legacyRows: LegacyKnowledgeSummary[] = []
    const preservedLegacyRows: unknown[] = []
    for (const row of parsedIndex.rows) {
      const parsed = KnowledgeSchema.safeParse(row)
      if (parsed.success) {
        validRows.push(parsed.data)
        continue
      }

      const legacy = parseLegacySummary(row)
      if (legacy) {
        legacyRows.push(legacy)
        continue
      }

      addKnowledgeItem(report, {
        status: "failed",
        sourcePath: indexPath,
        reason: "knowledge-index-invalid",
        message: parsed.error.issues.map((issue) => issue.message).join("; "),
      })
    }

    if (legacyRows.length === 0) return report

    const existingNames = new Set(validRows.map((row) => row.name))
    const migratedRows: Knowledge[] = []
    for (const legacy of legacyRows) {
      const migrated = await migrateLegacyKnowledge({
        report,
        knowledgeRoot,
        legacy,
        existingNames,
      })
      if (migrated) {
        migratedRows.push(migrated)
        existingNames.add(migrated.name)
      } else {
        const failedItem = report.items.find(
          (item) => item.legacyKnowledgeId === legacy.id && (item.status === "failed" || item.status === "skipped"),
        )
        if (failedItem) {
          const originalRow = parsedIndex.rows.find((row) => isRecord(row) && row.id === legacy.id)
          if (originalRow) {
            preservedLegacyRows.push(originalRow)
            addKnowledgeItem(report, {
              status: "skipped",
              sourcePath: indexPath,
              legacyKnowledgeId: legacy.id,
              name: legacy.name,
              reason: "legacy-preserved",
              message: `Migration failed/skipped - original row preserved in index. Reason: ${failedItem.reason}`,
            })
          }
        }
      }
    }

    try {
      await writeBackup(path.join(knowledgeRoot, "knowledge.v1.backup.json"), originalIndex)
      const nextRows = [...validRows, ...migratedRows, ...preservedLegacyRows].sort((a, b) => {
        const aName = isRecord(a) && typeof a.name === "string" ? a.name : ""
        const bName = isRecord(b) && typeof b.name === "string" ? b.name : ""
        return aName.localeCompare(bName)
      })
      await writeJsonAtomic(indexPath, nextRows)

      if (preservedLegacyRows.length > 0) {
        log.warn("preserved legacy rows in knowledge index after migration", {
          count: preservedLegacyRows.length,
          ids: preservedLegacyRows.map((row) => isRecord(row) && typeof row.id === "string" ? row.id : "unknown"),
        })
      }
    } catch (err) {
      const message = errorMessage(err)
      log.warn("failed to rewrite v1 knowledge index", { indexPath, error: message })
      addKnowledgeItem(report, {
        status: "failed",
        sourcePath: indexPath,
        targetPath: indexPath,
        reason: "rewrite-failed",
        message,
      })
    }

    return report
  }
}

// Known limitation: writeJsonAtomic does not share KnowledgeStorage's write queue.
// Theoretical race: user KB write between migration read and rename could be overwritten.
// Trade-off accepted: window ~100ms, atomic rename prevents corruption, cannot reuse
// KnowledgeStorage.save (it only accepts Knowledge objects, not raw legacy rows).
async function writeJsonAtomic(file: string, value: unknown) {
  await fs.mkdir(path.dirname(file), { recursive: true })
  const tmp = `${file}.${randomUUID()}.tmp`
  await fs.writeFile(tmp, `${JSON.stringify(value, null, 2)}\n`, "utf8")
  await fs.rename(tmp, file)
}

function createReport(): KnowledgeMigrationReport {
  return {
    summary: {
      migrated: 0,
      skipped: 0,
      failed: 0,
      documentsMigrated: 0,
      documentsSkipped: 0,
      documentsFailed: 0,
    },
    items: [],
  }
}

function addKnowledgeItem(report: KnowledgeMigrationReport, item: KnowledgeMigrationItem) {
  report.items.push(item)
  // legacy-preserved items are internal bookkeeping and should not affect summary counts
  const isLegacyPreserved = item.reason === "legacy-preserved"
  if (item.status === "migrated" && !isLegacyPreserved) report.summary.migrated++
  if (item.status === "skipped" && !isLegacyPreserved) report.summary.skipped++
  if (item.status === "failed" && !isLegacyPreserved) report.summary.failed++

  for (const document of item.documents ?? []) {
    if (document.status === "migrated") report.summary.documentsMigrated++
    if (document.status === "skipped") report.summary.documentsSkipped++
    if (document.status === "failed") report.summary.documentsFailed++
  }
}

function parseIndex(text: string):
  | {
      ok: true
      rows: unknown[]
    }
  | {
      ok: false
      message: string
    } {
  try {
    const parsed = JSON.parse(text) as unknown
    return Array.isArray(parsed) ? { ok: true, rows: parsed } : { ok: false, message: "knowledge.json is not an array" }
  } catch (err) {
    return { ok: false, message: errorMessage(err) }
  }
}

function parseLegacySummary(row: unknown): LegacyKnowledgeSummary | undefined {
  if (!isRecord(row)) return undefined
  const id = stringValue(row.id)
  const name = stringValue(row.name)
  if (!id || !id.startsWith("kb_") || !name) return undefined
  return {
    id,
    name,
    createdTime: stringValue(row.createdTime),
    updatedTime: stringValue(row.updatedTime),
  }
}

async function migrateLegacyKnowledge(input: {
  report: KnowledgeMigrationReport
  knowledgeRoot: string
  legacy: LegacyKnowledgeSummary
  existingNames: Set<string>
}): Promise<Knowledge | undefined> {
  const legacyDir = path.join(input.knowledgeRoot, input.legacy.id)
  const configPath = path.join(legacyDir, "config.json")
  const mappedKnowledgeId = stableUuid(`infcode-v1-knowledge:${input.legacy.id}`)
  const targetConfigPath = path.join(input.knowledgeRoot, mappedKnowledgeId, "config.json")

  if (input.existingNames.has(input.legacy.name)) {
    addKnowledgeItem(input.report, {
      status: "skipped",
      sourcePath: configPath,
      legacyKnowledgeId: input.legacy.id,
      mappedKnowledgeId,
      name: input.legacy.name,
      reason: "name-conflict",
    })
    return undefined
  }

  const targetDir = path.join(input.knowledgeRoot, mappedKnowledgeId)
  if (await exists(targetDir)) {
    const resumed = await readTargetKnowledge(targetConfigPath)
    if (resumed.ok) {
      addKnowledgeItem(input.report, {
        status: "migrated",
        sourcePath: configPath,
        targetPath: targetConfigPath,
        legacyKnowledgeId: input.legacy.id,
        mappedKnowledgeId,
        name: resumed.value.name,
        reason: "target-resumed",
      })
      return resumed.value
    }
  }

  const config = await readLegacyConfig(configPath, input.legacy)
  if (!config.ok) {
    addKnowledgeItem(input.report, {
      status: "failed",
      sourcePath: configPath,
      legacyKnowledgeId: input.legacy.id,
      mappedKnowledgeId,
      name: input.legacy.name,
      reason: config.reason,
      message: config.message,
    })
    return undefined
  }

  const prepared: PreparedDocument[] = []
  const documentReports: KnowledgeMigrationDocumentItem[] = []
  for (const document of config.value.documents) {
    const next = await prepareDocument({
      knowledgeRoot: input.knowledgeRoot,
      legacyKnowledgeId: input.legacy.id,
      legacyDir,
      mappedKnowledgeId,
      document,
    })
    documentReports.push(next.report)
    if (next.document) prepared.push(next.document)
  }

  if (prepared.length === 0) {
    addKnowledgeItem(input.report, {
      status: "failed",
      sourcePath: configPath,
      targetPath: targetConfigPath,
      legacyKnowledgeId: input.legacy.id,
      mappedKnowledgeId,
      name: config.value.name,
      reason: "no-readable-documents",
      documents: documentReports,
    })
    return undefined
  }

  const stamp = config.value.updatedTime ?? input.legacy.updatedTime ?? new Date().toISOString()
  const knowledge: Knowledge = {
    id: mappedKnowledgeId,
    name: config.value.name,
    status: "not_built",
    documents: prepared.map((item) => item.document),
    createdTime: config.value.createdTime ?? input.legacy.createdTime,
    updatedTime: stamp,
    requiresManualIndex: true,
  }

  try {
    await writeBackup(path.join(legacyDir, "config.v1.backup.json"), config.value.originalText)
    for (const item of prepared) {
      await fs.mkdir(path.dirname(item.report.targetPath!), { recursive: true })
      await fs.writeFile(item.report.targetPath!, item.content, "utf8")
    }
    await writeJsonAtomic(targetConfigPath, knowledge)
    addKnowledgeItem(input.report, {
      status: "migrated",
      sourcePath: configPath,
      targetPath: targetConfigPath,
      backupPath: path.join(legacyDir, "config.v1.backup.json"),
      legacyKnowledgeId: input.legacy.id,
      mappedKnowledgeId,
      name: config.value.name,
      documents: documentReports,
    })
    return knowledge
  } catch (err) {
    const message = errorMessage(err)
    log.warn("failed to migrate v1 knowledge", { configPath, targetConfigPath, error: message })
    addKnowledgeItem(input.report, {
      status: "failed",
      sourcePath: configPath,
      targetPath: targetConfigPath,
      legacyKnowledgeId: input.legacy.id,
      mappedKnowledgeId,
      name: config.value.name,
      reason: "rewrite-failed",
      message,
      documents: documentReports,
    })
    return undefined
  }
}

async function readTargetKnowledge(
  targetConfigPath: string,
): Promise<{ ok: true; value: Knowledge } | { ok: false; reason: "config-missing" | "config-invalid"; message?: string }> {
  if (!(await exists(targetConfigPath))) return { ok: false, reason: "config-missing" }

  let text: string
  try {
    text = await fs.readFile(targetConfigPath, "utf8")
  } catch (err) {
    return { ok: false, reason: "config-invalid", message: errorMessage(err) }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text) as unknown
  } catch (err) {
    return { ok: false, reason: "config-invalid", message: errorMessage(err) }
  }

  const validated = KnowledgeSchema.safeParse(parsed)
  if (!validated.success) {
    return {
      ok: false,
      reason: "config-invalid",
      message: validated.error.issues.map((issue) => issue.message).join("; "),
    }
  }
  return { ok: true, value: validated.data }
}

async function readLegacyConfig(
  configPath: string,
  summary: LegacyKnowledgeSummary,
): Promise<
  | { ok: true; value: LegacyKnowledgeConfig }
  | { ok: false; reason: "config-missing" | "config-invalid"; message?: string }
> {
  if (!(await exists(configPath))) return { ok: false, reason: "config-missing" }

  let originalText: string
  try {
    originalText = await fs.readFile(configPath, "utf8")
  } catch (err) {
    return { ok: false, reason: "config-invalid", message: errorMessage(err) }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(originalText) as unknown
  } catch (err) {
    return { ok: false, reason: "config-invalid", message: errorMessage(err) }
  }
  if (!isRecord(parsed)) return { ok: false, reason: "config-invalid", message: "config root is not an object" }

  const name = stringValue(parsed.name) ?? summary.name
  if (!name || !Array.isArray(parsed.documents)) {
    return { ok: false, reason: "config-invalid", message: "config is missing name or documents" }
  }

  return {
    ok: true,
    value: {
      name,
      createdTime: stringValue(parsed.createdTime) ?? summary.createdTime,
      updatedTime: stringValue(parsed.updatedTime) ?? summary.updatedTime,
      documents: parsed.documents.filter(isRecord),
      originalText,
    },
  }
}

async function prepareDocument(input: {
  knowledgeRoot: string
  legacyKnowledgeId: string
  legacyDir: string
  mappedKnowledgeId: string
  document: Record<string, unknown>
}): Promise<
  | {
      document: PreparedDocument
      report: KnowledgeMigrationDocumentItem
    }
  | {
      document: undefined
      report: KnowledgeMigrationDocumentItem
    }
> {
  const legacyDocumentId = stringValue(input.document.id)
  const name = stringValue(input.document.name) ?? "legacy-document.md"
  const mappedDocumentId = mapDocumentId(input.legacyKnowledgeId, legacyDocumentId ?? name)
  const targetPath = path.join(input.knowledgeRoot, input.mappedKnowledgeId, KnowledgeStorage.documentRelativePath(mappedDocumentId))
  const sourcePath = await findSourceDocumentPath(input.legacyDir, input.document)

  if (!sourcePath) {
    return {
      document: undefined,
      report: {
        status: "failed",
        legacyDocumentId,
        mappedDocumentId,
        name,
        targetPath,
        reason: "document-missing",
      },
    }
  }

  let content: string
  let stat: Awaited<ReturnType<typeof fs.stat>>
  try {
    ;[content, stat] = await Promise.all([fs.readFile(sourcePath, "utf8"), fs.stat(sourcePath)])
  } catch (err) {
    return {
      document: undefined,
      report: {
        status: "failed",
        legacyDocumentId,
        mappedDocumentId,
        name,
        sourcePath,
        targetPath,
        reason: "document-unreadable",
        message: errorMessage(err),
      },
    }
  }

  const stamp =
    stringValue(input.document.lastUpdateTime) ??
    stringValue(input.document.updatedTime) ??
    stringValue(input.document.fileModifiedTime) ??
    stat.mtime.toISOString()
  const document: KnowledgeDocument = {
    id: mappedDocumentId,
    name,
    status: "not_indexed",
    path: KnowledgeStorage.documentRelativePath(mappedDocumentId),
    lastUpdateTime: stamp,
    contentHash: KnowledgeStorage.hash(content),
    fileSize: Buffer.byteLength(content),
    fileModifiedTime: stringValue(input.document.fileModifiedTime) ?? stat.mtime.toISOString(),
  }

  const report: KnowledgeMigrationDocumentItem = {
    status: "migrated",
    legacyDocumentId,
    mappedDocumentId,
    name,
    sourcePath,
    targetPath,
  }

  return {
    document: {
      document,
      content,
      report,
    },
    report,
  }
}

async function findSourceDocumentPath(legacyDir: string, document: Record<string, unknown>): Promise<string | undefined> {
  const candidates: string[] = []
  const legacyPath = stringValue(document.path)
  const name = stringValue(document.name)
  if (legacyPath) {
    candidates.push(path.isAbsolute(legacyPath) ? legacyPath : path.join(legacyDir, legacyPath))
    candidates.push(path.join(legacyDir, path.basename(legacyPath)))
  }
  if (name) candidates.push(path.join(legacyDir, path.basename(name)))

  for (const candidate of unique(candidates)) {
    if (await isFile(candidate)) return candidate
  }
  return undefined
}

function mapDocumentId(legacyKnowledgeId: string, legacyDocumentId: string) {
  const wrappedUuid = /^doc_([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.exec(
    legacyDocumentId,
  )?.[1]
  if (wrappedUuid && KNOWLEDGE_ENTITY_ID_PATTERN.test(wrappedUuid)) return wrappedUuid.toLowerCase()
  if (KNOWLEDGE_ENTITY_ID_PATTERN.test(legacyDocumentId)) return legacyDocumentId.toLowerCase()
  return stableUuid(`infcode-v1-document:${legacyKnowledgeId}:${legacyDocumentId}`)
}

function stableUuid(seed: string) {
  const hex = createHash("sha256").update(seed).digest("hex").slice(0, 32).split("")
  hex[12] = "5"
  hex[16] = "8"
  return `${hex.slice(0, 8).join("")}-${hex.slice(8, 12).join("")}-${hex.slice(12, 16).join("")}-${hex
    .slice(16, 20)
    .join("")}-${hex.slice(20).join("")}`
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined
}

function unique(values: string[]) {
  return [...new Set(values)]
}
